import {
    Alert,
    Box,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import CustomSnackbar from "../../components/CustomSnackbar";
import Item from "../../components/Item";
import PuzzleCoinsService from "./PuzzleCoinsService";

const TAB_COMPLETIONS = 0;
const TAB_CATALOG = 1;
const CATALOG_TYPE_STORAGE_KEY = "puzzleCoins.catalogType";

function readCatalogType() {
    try {
        const stored = window.localStorage.getItem(CATALOG_TYPE_STORAGE_KEY);
        if (stored === "SLIDING" || stored === "JIGSAW") return stored;
    } catch {
        /* ignore */
    }
    return "JIGSAW";
}

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function apiFailureMessage(result, fallback = "Request failed") {
    if (result && typeof result === "object" && result.code === "FAILURE") {
        return result.message || fallback;
    }
    return null;
}

function puzzleTypeChipColor(type) {
    if (type === "SLIDING") return "primary";
    if (type === "JIGSAW") return "secondary";
    if (type === "WORD_WHEEL") return "success";
    if (type === "PURCHASE") return "warning";
    if (type === "HINT") return "error";
    return "default";
}

function puzzleTypeLabel(type) {
    if (type === "HINT") return "Web hint";
    if (type === "PURCHASE") return "Purchase";
    if (type === "SLIDING") return "Sliding";
    if (type === "JIGSAW") return "Jigsaw";
    if (type === "WORD_WHEEL") return "Word Wheel";
    return type || "—";
}

function resolveCloudUserLabel(cloudUser) {
    if (!cloudUser) return null;
    const first = cloudUser.firstName;
    const last = cloudUser.lastName;
    if (first && first.trim()) {
        if (last && last.trim()) {
            return `${first.trim()} ${last.trim()}`.trim();
        }
        return first.trim();
    }
    if (cloudUser.email && cloudUser.email.trim()) {
        return cloudUser.email.trim();
    }
    return cloudUser.id || null;
}

function NameCell({ label, title }) {
    if (!label) {
        return "—";
    }
    return (
        <Tooltip title={title || label}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Tooltip>
    );
}

function PagedGrid({
    rows,
    columns,
    rowCount,
    pageModel,
    onPageModelChange,
    sort,
    onSortChange,
    loading,
}) {
    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationModel={pageModel}
            onPaginationModelChange={onPageModelChange}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            sortingMode="server"
            sortModel={sort}
            onSortModelChange={onSortChange}
            loading={loading}
            disableRowSelectionOnClick
            autoHeight
            hideFooterSelectedRowCount
        />
    );
}

export default function PuzzleCoinsMain() {
    const [tab, setTab] = useState(TAB_COMPLETIONS);
    const [loading, setLoading] = useState(false);

    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sort, setSort] = useState([{ field: "earnedAt", sort: "desc" }]);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);

    const [filterUserId, setFilterUserId] = useState("");
    const [filterPuzzleType, setFilterPuzzleType] = useState("");

    const [catalogSlidingRows, setCatalogSlidingRows] = useState([]);
    const [catalogJigsawRows, setCatalogJigsawRows] = useState([]);
    const [catalogType, setCatalogType] = useState(readCatalogType);

    const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

    const showSnack = useCallback((severity, message) => {
        setSnackbar({ open: true, severity, message });
    }, []);

    useEffect(() => {
        setPageModel((prev) => ({ ...prev, page: 0 }));
    }, [tab, filterUserId, filterPuzzleType]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (tab === TAB_CATALOG) {
                setLoading(true);
                try {
                    const result = await PuzzleCoinsService.getCatalog();
                    if (cancelled) return;
                    const err = apiFailureMessage(result);
                    if (err) {
                        showSnack("error", err);
                        setCatalogSlidingRows([]);
                        setCatalogJigsawRows([]);
                        return;
                    }
                    const parsed = PuzzleCoinsService.parseCatalog(result);
                    setCatalogSlidingRows(parsed.slidingRows);
                    setCatalogJigsawRows(parsed.jigsawRows);
                } catch (e) {
                    Logger.error("PuzzleCoinsMain catalog:", e);
                    if (!cancelled) {
                        showSnack("error", "Failed to load coin catalog");
                        setCatalogSlidingRows([]);
                        setCatalogJigsawRows([]);
                    }
                } finally {
                    if (!cancelled) setLoading(false);
                }
                return;
            }

            setLoading(true);
            try {
                const result = await PuzzleCoinsService.listCoins(pageModel, sort, {
                    userId: filterUserId.trim(),
                    puzzleType: filterPuzzleType.trim(),
                });
                if (cancelled) return;
                const err = apiFailureMessage(result);
                if (err) {
                    showSnack("error", err);
                    setRows([]);
                    setRowCount(0);
                    return;
                }
                setRows(
                    (result?.content || []).map((row) => ({
                        ...row,
                        id: row.id,
                    })),
                );
                setRowCount(PuzzleCoinsService.totalElements(result));
            } catch (e) {
                Logger.error("PuzzleCoinsMain list:", e);
                if (!cancelled) {
                    setRows([]);
                    setRowCount(0);
                    showSnack("error", "Failed to load puzzle coins");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [tab, pageModel, sort, filterUserId, filterPuzzleType, showSnack]);

    const handleCatalogType = (next) => {
        setCatalogType(next);
        try {
            window.localStorage.setItem(CATALOG_TYPE_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const visibleCatalogRows = catalogType === "SLIDING" ? catalogSlidingRows : catalogJigsawRows;

    const handleTabChange = (_e, next) => {
        setTab(next);
        if (next === TAB_COMPLETIONS) setSort([{ field: "earnedAt", sort: "desc" }]);
    };

    const completionColumns = useMemo(
        () => [
            {
                field: "cloudUser",
                headerName: "User",
                flex: 1,
                minWidth: 180,
                valueGetter: (_value, row) => resolveCloudUserLabel(row.cloudUser),
                renderCell: (params) => (
                    <NameCell
                        label={resolveCloudUserLabel(params.row.cloudUser)}
                        title={params.row.cloudUser?.id}
                    />
                ),
            },
            {
                field: "puzzleType",
                headerName: "Type",
                width: 110,
                renderCell: (params) => (
                    <Chip
                        label={puzzleTypeLabel(params.value)}
                        size="small"
                        color={puzzleTypeChipColor(params.value)}
                        variant="outlined"
                    />
                ),
            },
            {
                field: "puzzleName",
                headerName: "Puzzle",
                flex: 1.2,
                minWidth: 220,
                renderCell: (params) => (
                    <NameCell label={params.value} title={params.row.puzzleId} />
                ),
            },
            { field: "coins", headerName: "Coins", width: 90, type: "number" },
            {
                field: "earnedAt",
                headerName: "Earned",
                width: 130,
                valueFormatter: (value) => formatEpochMs(value),
            },
        ],
        [],
    );

    const catalogColumns = useMemo(
        () => [
            {
                field: "puzzleType",
                headerName: "Type",
                width: 110,
                renderCell: (params) => (
                    <Chip
                        label={puzzleTypeLabel(params.value)}
                        size="small"
                        color={puzzleTypeChipColor(params.value)}
                        variant="outlined"
                    />
                ),
            },
            {
                field: "size",
                headerName: "Size",
                width: 100,
                valueFormatter: (value) => `${value}×${value}`,
            },
            { field: "difficulty", headerName: "Difficulty", width: 120 },
            { field: "coins", headerName: "Coins", width: 90, type: "number" },
        ],
        [],
    );

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Item>
                        {tab === TAB_CATALOG && visibleCatalogRows.length > 0 ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                {visibleCatalogRows.length}{" "}
                                {catalogType === "SLIDING" ? "sliding" : "jigsaw"} rule
                                {visibleCatalogRows.length === 1 ? "" : "s"}
                            </Typography>
                        ) : null}
                        <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                            <Tab label="Completions" />
                            <Tab label="Coin catalog" />
                        </Tabs>
                    </Item>
                </Grid>

                {tab === TAB_COMPLETIONS ? (
                    <Grid size={{ xs: 12 }}>
                        <Item
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                alignItems: "center",
                            }}
                        >
                            <TextField
                                size="small"
                                label="User ID"
                                value={filterUserId}
                                onChange={(e) => setFilterUserId(e.target.value)}
                                sx={{ minWidth: 220 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel id="puzzle-coins-type-filter-label">Puzzle type</InputLabel>
                                <Select
                                    labelId="puzzle-coins-type-filter-label"
                                    label="Puzzle type"
                                    value={filterPuzzleType}
                                    onChange={(e) => setFilterPuzzleType(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="HINT">Web hint</MenuItem>
                                    <MenuItem value="SLIDING">Sliding</MenuItem>
                                    <MenuItem value="JIGSAW">Jigsaw</MenuItem>
                                    <MenuItem value="WORD_WHEEL">Word Wheel</MenuItem>
                                </Select>
                            </FormControl>
                        </Item>
                    </Grid>
                ) : null}

                <Grid size={{ xs: 12 }}>
                    <Item>
                        {tab === TAB_CATALOG ? (
                            <>
                            <Alert severity="info" sx={{ mb: 1.5 }}>
                                These coins are given to the user when a puzzle is finished.
                            </Alert>
                            <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                                <Chip
                                    label="Sliding"
                                    clickable
                                    color={catalogType === "SLIDING" ? "primary" : "default"}
                                    variant={catalogType === "SLIDING" ? "filled" : "outlined"}
                                    onClick={() => handleCatalogType("SLIDING")}
                                    sx={{ minHeight: 44, fontWeight: 700 }}
                                />
                                <Chip
                                    label="Jigsaw"
                                    clickable
                                    color={catalogType === "JIGSAW" ? "secondary" : "default"}
                                    variant={catalogType === "JIGSAW" ? "filled" : "outlined"}
                                    onClick={() => handleCatalogType("JIGSAW")}
                                    sx={{ minHeight: 44, fontWeight: 700 }}
                                />
                            </Box>
                            <DataGrid
                                rows={visibleCatalogRows}
                                columns={catalogColumns}
                                loading={loading}
                                disableRowSelectionOnClick
                                autoHeight
                                hideFooter={visibleCatalogRows.length <= 25}
                                pageSizeOptions={[25, 50]}
                                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                            />
                            </>
                        ) : (
                            <PagedGrid
                                rows={rows}
                                columns={completionColumns}
                                rowCount={rowCount}
                                pageModel={pageModel}
                                onPageModelChange={setPageModel}
                                sort={sort}
                                onSortChange={setSort}
                                loading={loading}
                            />
                        )}
                    </Item>
                </Grid>
            </Grid>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
